'use client';

import Link from '@eliadmin/core/src/components/link';
import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import type { PropsWithChildren } from 'react';

import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { usePathname } from 'next/navigation';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import { useId, useState } from 'react';
import { isOrganizedOffersMenuItem } from './menu-item-identity';

export type CategoryMenuProps = {
  className?: string;
};

export function CategoryMenu(props: PropsWithChildren<CategoryMenuProps>) {
  return (
    <ul className={cn('flex flex-row items-center gap-0', 'ea-category-menu', props.className)}>
      {props.children}
    </ul>
  );
}

export type CategoryMenuItemProps = {
  categories: CategoriesEntity[];
  title: string;
  href?: string;
  onClick?: () => void;
  order?: number;
  index: number;
  isLast?: boolean;
  className?: {
    root?: string;
    link?: string;
  };
};

export function CategoryMenuItem(props: CategoryMenuItemProps) {
  const organizedOffers = isOrganizedOffersMenuItem(props.href);
  const LinkComp = organizedOffers ? 'button' : props.href ? (isRelativeHref(getSafeHref(props.href)) ? Link : 'a') : 'span';
  const pathname = usePathname();
  const [showSubmenu, setShowSubmenu] = useState(false);
  const [showChildSubmenu, setShowChildSubmenu] = useState<string | null>(null);
  const submenuId = useId();

  const isActive = props.href && pathname === getSafeHref(props.href);

  const handleMouseEnter = () => {
    if (organizedOffers) {
      setShowSubmenu(true);
    }
  };

  const handleMouseLeave = () => {
    setShowSubmenu(false);
    setShowChildSubmenu(null);
  };

  const handleCategoryMouseEnter = (categoryId: string) => {
    if (props.categories.find(cat => String(cat.id) === categoryId)?.childs?.length) {
      setShowChildSubmenu(categoryId);
    }
  };

  const handleCategoryMouseLeave = () => {
    setShowChildSubmenu(null);
  };

  return (
    <li
      style={{ order: props.order }}
      className={cn(
        'relative flex flex-row whitespace-nowrap',
        'ea-category-menu-item',
        props.className?.root,
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onKeyDown={event => {
        if (organizedOffers && event.key === 'Escape') {
          setShowSubmenu(false);
          setShowChildSubmenu(null);
          event.currentTarget.querySelector('button')?.focus();
        }
      }}
    >
      <LinkComp
        href={organizedOffers ? undefined : getSafeHref(props?.href ?? '#')}
        type={organizedOffers ? 'button' : undefined}
        aria-expanded={organizedOffers ? showSubmenu : undefined}
        aria-controls={organizedOffers && showSubmenu ? submenuId : undefined}
        className={cn(
          'flex items-center justify-center gap-2 rounded-[12px] px-4 py-4 text-sm font-normal text-foreground transition-all 2xl:text-base',
          'ea-category-menu-item__link',
          isActive ? 'bg-secondary font-bold' : 'hover:bg-secondary/40',
        )}
        onClick={() => {
          props.onClick?.();
          if (organizedOffers) setShowSubmenu(open => !open);
        }}
      >
        {props.title}{' '}
        {organizedOffers && <MdKeyboardArrowDown size={20} className="text-primary" />}
      </LinkComp>

      {showSubmenu && organizedOffers && (
        <ul
          id={submenuId}
          className={cn(
            'absolute left-0 top-full z-[100] mt-0 w-max rounded-[12px] bg-white shadow-lg',
            'ea-category-menu__submenu-wrapper',
          )}
        >
          {props.categories.map((category, index) => (
            <li
              key={category.id}
              className="relative"
              onMouseEnter={() => handleCategoryMouseEnter(String(category.id))}
              onMouseLeave={handleCategoryMouseLeave}
            >
              <Link
                href={`${category.slug}?type=uskutocnene`}
                className={cn(
                  'group relative flex w-full items-center justify-between whitespace-nowrap px-4 py-3 transition-all hover:bg-[#F6F6F6]',
                  index === 0 && 'rounded-t-[12px]',
                  index === props.categories.length - 1 && 'rounded-b-[12px]',
                )}
              >
                <p className="text-base font-normal text-foreground">{category.name}</p>
                {category?.childs && category.childs.length > 0 && (
                  <MdKeyboardArrowRight size={20} className="text-primary" />
                )}
              </Link>
              {category?.childs &&
                category.childs.length > 0 &&
                showChildSubmenu === String(category.id) && (
                  <ul
                    className={cn(
                      'absolute left-full top-0 z-[100] mt-0 w-max rounded-[12px] bg-white shadow-lg',
                      'ea-category-menu__child-submenu-wrapper',
                    )}
                  >
                    {category.childs.map((child, childIndex) => (
                      <li key={child.id}>
                        <Link
                          href={`${child.slug}?type=uskutocnene`}
                          className={cn(
                            'flex w-full items-center justify-between whitespace-nowrap px-4 py-3 transition-all hover:bg-[#F6F6F6]',
                            childIndex === 0 && 'rounded-t-[12px]',
                            childIndex === (category.childs?.length ?? 0) - 1 && 'rounded-b-[12px]',
                          )}
                        >
                          <p className="text-base font-normal text-foreground">{child.name}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
